import { Box, Card, Grid, TableContainer, Typography } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import NewRecordable from "./NewRecordable";
import OrderSidebar from "./OrderSidebar";
import RecordableBreadcrumbs from "./RecordableBreadcrumbs";
import RecordableFilters from "./RecordableFilters";
import RecordableHeader from "./RecordableHeader";
import RecordableTable from "./RecordableTable";
import VersionUpload from "./VersionUpload";
import PrintDocumentBarcodes from "./PrintDocumentBarcodes";
import { printVersionBarcode } from "./VersionBarcodePrint";
import { PendingRecordableBarcode, Recordable, RecordableType, RecordableVersionBarcode } from "./types";
import { copyRecordableInfoToClipboard } from "./utils/recordableUtils";
import { useSnackbar } from "notistack";

export default function Recordables() {
  const [searchParams] = useSearchParams();
  const order = searchParams.get("order");

  const { enqueueSnackbar } = useSnackbar();

  const [newRecordableOpen, setNewRecordableOpen] = useState<boolean>(false);
  const [printBarcodesOpen, setPrintBarcodesOpen] = useState<boolean>(false);
  const handleCloseNewRecordable = () => setNewRecordableOpen(false);
  const handleOpenNewRecordable = () => setNewRecordableOpen(true);
  const [openVersionUpload, setOpenVersionUpload] = useState<boolean>(false);
  const [generatingVersionBarcodeId, setGeneratingVersionBarcodeId] = useState<number | null>(null);

  const [recordableTypes, setRecordableTypes] = useState<RecordableType[]>([]);
  const [recordables, setRecordables] = useState<Recordable[]>([]);
  const [pendingBarcodes, setPendingBarcodes] = useState<PendingRecordableBarcode[]>([]);
  const [selectedRecordableType, setSelectedRecordableType] = useState<RecordableType | null>(null);
  const [selectedRecordable, setSelectedRecordable] = useState<Recordable | null>(null);
  const [isReorderMode, setIsReorderMode] = useState<boolean>(false);
  const [isSavingOrder, setIsSavingOrder] = useState<boolean>(false);

  const getAndSetRecordableTypes = async () => {
    const { data } = await axios.get(`/api/recordablestracking/recordables/getTypes`);
    setRecordableTypes(data);
  };

  const getAndSetRecordables = async () => {
    const { data } = await axios.get(`/api/recordablestracking/recordables/getByOrderNumber?orderNumber=${order}`);
    setRecordables(data);
  };

  const getAndSetPendingBarcodes = async () => {
    const { data } = await axios.get(
      `/api/recordablestracking/recordables/barcodes/getPendingByOrderNumber?orderNumber=${order}`
    );
    setPendingBarcodes(data);
  };

  useEffect(() => {
    getAndSetRecordableTypes();
    getAndSetRecordables();
    getAndSetPendingBarcodes();
  }, []);

  const activeRecordables = recordables.filter((r) => !r.isDeleted);
  const deletedDocuments = recordables.filter((r) => r.isDeleted);

  const filteredRecordables = recordables.filter(
    (r) => (isReorderMode || !selectedRecordableType || r.type === selectedRecordableType.id) && !r.isDeleted
  );

  const filteredDeletedDocuments = isReorderMode
    ? deletedDocuments
    : selectedRecordableType
    ? recordables.filter((r) => r.type === selectedRecordableType.id && r.isDeleted)
    : deletedDocuments;

  const handleCopy = async (recordables: Recordable[]) => {
    const success = await copyRecordableInfoToClipboard(recordables);

    if (success) {
      enqueueSnackbar("Summary of incomplete items was copied to clipboard. You can now paste it to an email.", {
        variant: "success",
        autoHideDuration: 3500,
        anchorOrigin: { vertical: "bottom", horizontal: "center" }
      });
    } else {
      enqueueSnackbar("There was a problem copying the data. Please try again", {
        variant: "error",
        autoHideDuration: 3500,
        anchorOrigin: { vertical: "bottom", horizontal: "center" }
      });
    }
  };

  const handleToggleReorderMode = () => {
    setIsReorderMode((prev) => !prev);
  };

  const handleGenerateVersionBarcode = async (recordable: Recordable) => {
    if (!order || generatingVersionBarcodeId !== null) {
      return;
    }

    try {
      setGeneratingVersionBarcodeId(recordable.id);
      const { data } = await axios.post<RecordableVersionBarcode>(
        "/api/recordablestracking/recordables/barcodes/generateVersion",
        {
          orderNumber: order,
          recordableId: recordable.id
        }
      );
      await printVersionBarcode(order, data);
      await getAndSetPendingBarcodes();
      enqueueSnackbar("Version QR code prepared for printing.", { variant: "success" });
    } catch {
      enqueueSnackbar("The version QR code could not be prepared for printing.", { variant: "error" });
    } finally {
      setGeneratingVersionBarcodeId(null);
    }
  };

  const handleReorderRecordables = async (reorderedRecordables: Recordable[]) => {
    if (isSavingOrder) {
      return;
    }

    const sortIndexes = reorderedRecordables.map((r, index) => ({
      id: r.id,
      sortIndex: index
    }));

    const reorderedIds = new Set(reorderedRecordables.map((r) => r.id));
    const nextRecordables = [
      ...reorderedRecordables.map((r, index) => ({ ...r, sortIndex: index })),
      ...recordables.filter((r) => !reorderedIds.has(r.id))
    ];

    try {
      setIsSavingOrder(true);
      setRecordables(nextRecordables);
      await axios.post("/api/recordablestracking/recordables/updateSortIndexes", { sortIndexes });
      enqueueSnackbar("Document order updated successfully.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to update document order. Please try again.", { variant: "error" });
      await getAndSetRecordables();
    } finally {
      setIsSavingOrder(false);
    }
  };

  return (
    <>
      <RecordableHeader
        handleOpenNewRecordable={handleOpenNewRecordable}
        handleOpenPrintBarcodes={() => setPrintBarcodesOpen(true)}
        isReorderMode={isReorderMode}
      />
      <RecordableBreadcrumbs />
      <Grid container spacing={2}>
        <Grid item xs={9}>
          <TableContainer component={Card}>
            <RecordableFilters
              options={recordableTypes}
              setSelectedRecordableType={setSelectedRecordableType}
              copyToClipboard={() => handleCopy(filteredRecordables)}
              disabled={isReorderMode}
              disableReorder={isSavingOrder || (!isReorderMode && activeRecordables.length < 2)}
              isReorderMode={isReorderMode}
              onToggleReorderMode={handleToggleReorderMode}
            />
            <RecordableTable
              isDeleted={false}
              data={filteredRecordables}
              setSelectedRecordable={setSelectedRecordable}
              setOpenVersionUpload={setOpenVersionUpload}
              openVersionBarcode={handleGenerateVersionBarcode}
              getAndSetRecordables={getAndSetRecordables}
              isReorderMode={isReorderMode}
              isReorderDisabled={isSavingOrder}
              onReorder={handleReorderRecordables}
            />
            <Box mt={4}>
              <RecordableTable
                isDeleted
                data={filteredDeletedDocuments}
                setSelectedRecordable={setSelectedRecordable}
                setOpenVersionUpload={setOpenVersionUpload}
                openVersionBarcode={handleGenerateVersionBarcode}
                getAndSetRecordables={getAndSetRecordables}
              />
            </Box>
            {recordables.length === 0 && pendingBarcodes.length === 0 && (
              <Typography padding={2}>No recordables available</Typography>
            )}
          </TableContainer>
        </Grid>
        <Grid item xs={3}>
          <OrderSidebar />
        </Grid>
      </Grid>
      {newRecordableOpen && (
        <NewRecordable
          open={newRecordableOpen}
          onClose={handleCloseNewRecordable}
          updateRecordableData={getAndSetRecordables}
        />
      )}
      {printBarcodesOpen && order && (
        <PrintDocumentBarcodes
          open={printBarcodesOpen}
          orderNumber={order}
          onClose={() => {
            setPrintBarcodesOpen(false);
            getAndSetPendingBarcodes();
          }}
        />
      )}
      {openVersionUpload && (
        <VersionUpload
          open={openVersionUpload}
          onClose={() => setOpenVersionUpload(false)}
          updateData={getAndSetRecordables}
          recordableId={selectedRecordable?.id}
          documentType={selectedRecordable?.typeDescription}
          additionalDescription={selectedRecordable?.description}
          statusDescription={selectedRecordable?.statusDescription}
          newVersionCount={selectedRecordable?.documents?.length ? selectedRecordable?.documents?.length + 1 : 0}
        />
      )}
    </>
  );
}
